import React, { useState, useMemo } from 'react';
import { XMarkIcon, EnvelopeIcon, CheckIcon } from '@heroicons/react/24/outline';
import { getSupabaseClient } from '../lib/supabase';

interface RequestOffPlatformReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RequestOffPlatformReviewModal: React.FC<RequestOffPlatformReviewModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [reviewerName, setReviewerName] = useState('');
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const supabase = useMemo(() => {
    try {
      return getSupabaseClient();
    } catch (err) {
      console.error('Supabase client init failed', err);
      return null;
    }
  }, []);

  const resetForm = () => {
    setReviewerName('');
    setReviewerEmail('');
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!supabase) {
      setError('Unable to connect. Please try again.');
      return;
    }

    if (!reviewerName.trim()) {
      setError('Please enter the reviewer\'s name');
      return;
    }

    if (!reviewerEmail.trim()) {
      setError('Please enter the reviewer\'s email');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(reviewerEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        'off-platform-review-request',
        {
          body: {
            reviewer_name: reviewerName.trim(),
            reviewer_email: reviewerEmail.trim().toLowerCase(),
          },
        }
      );

      if (invokeError) {
        console.error('Error requesting review:', invokeError);
        setError(invokeError.message || 'Failed to send review request');
        return;
      }

      setSuccess(true);
    } catch (err) {
      console.error('Error requesting review:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-[#2C2731]/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="mx-4 w-full max-w-md rounded-3xl border border-[#2C2731]/20 bg-[#FFFCEF] text-[#2C2731] shadow-[0_24px_60px_-32px_rgba(20,10,30,0.7)]">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[#2C2731]/15 p-6">
            <h2 className="text-xl font-semibold text-[#2C2731]">Request a Review</h2>
            <button
              onClick={handleClose}
              className="p-1 text-[#2C2731]/60 transition-colors hover:text-[#2C2731]"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {success ? (
              // Success state
              <div className="flex flex-col items-center gap-4 py-4 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <CheckIcon className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-[#2C2731]">Request Sent!</h3>
                <p className="text-sm text-[#2C2731]/75">
                  We've sent an email to <span className="font-medium">{reviewerEmail}</span> with a
                  link to submit their review.
                </p>
                <button
                  onClick={handleClose}
                  className="mt-2 rounded-2xl bg-[#2C2731] px-6 py-2 text-sm font-semibold text-[#FFFCEF] transition-colors hover:bg-[#241F2A]"
                >
                  Done
                </button>
              </div>
            ) : (
              // Form state
              <form onSubmit={handleSubmit}>
                <p className="mb-4 text-sm text-[#2C2731]/75">
                  Send a review request to someone you've worked with outside of Guides Collective.
                  They'll receive an email with a link to submit their review.
                </p>

                {error && (
                  <div className="mb-4 rounded-2xl border border-[#B9383A]/20 bg-[#B9383A]/10 p-3 text-sm text-[#B9383A]">
                    {error}
                  </div>
                )}

                {/* Reviewer Name */}
                <div className="mb-4">
                  <label
                    htmlFor="reviewer-name"
                    className="mb-1.5 block text-sm font-medium text-[#2C2731]"
                  >
                    Reviewer's Name
                  </label>
                  <input
                    id="reviewer-name"
                    type="text"
                    value={reviewerName}
                    onChange={(e) => setReviewerName(e.target.value)}
                    placeholder="Enter their name"
                    className="w-full rounded-2xl border border-[#2C2731]/15 bg-white/80 px-4 py-2.5 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                  />
                </div>

                {/* Reviewer Email */}
                <div className="mb-6">
                  <label
                    htmlFor="reviewer-email"
                    className="mb-1.5 block text-sm font-medium text-[#2C2731]"
                  >
                    Reviewer's Email
                  </label>
                  <input
                    id="reviewer-email"
                    type="email"
                    value={reviewerEmail}
                    onChange={(e) => setReviewerEmail(e.target.value)}
                    placeholder="Enter their email address"
                    className="w-full rounded-2xl border border-[#2C2731]/15 bg-white/80 px-4 py-2.5 text-sm text-[#2C2731] placeholder:text-[#2C2731]/40 focus:border-[#2C2731] focus:outline-none focus:ring-1 focus:ring-[#2C2731]"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex flex-1 items-center justify-center space-x-2 rounded-2xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                      loading
                        ? 'cursor-not-allowed bg-[#2C2731]/30 text-[#FFFCEF]/70'
                        : 'bg-[#2C2731] text-[#FFFCEF] hover:bg-[#241F2A]'
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#FFFCEF] border-t-transparent" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <EnvelopeIcon className="h-4 w-4" />
                        <span>Send Request</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={loading}
                    className="rounded-2xl border border-[#2C2731]/25 px-4 py-2.5 text-sm font-semibold text-[#2C2731] transition-colors hover:bg-[#FFFCEF] disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RequestOffPlatformReviewModal;
